import React, { useState, useEffect } from "react";
import { Calendar } from "lucide-react";
import { customersAPI } from "../../services";
import "../../style/customerDetails.css";

const FinanceTransactionForm = ({
  transaction,
  customerId,
  customerName,
  onSave,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    date: new Date().toLocaleDateString("en-GB"),
    customerName: "",
    credit: "",
    debit: "",
    details: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(customerId || "");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = React.useRef(null);

  const isEditing = !!transaction;

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };
    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  useEffect(() => {
    if (transaction) {
      setFormData({
        date: transaction.date || new Date().toLocaleDateString("en-GB"),
        customerName: transaction.customerName || "",
        credit: transaction.credit ? `€${transaction.credit}` : "€0.0",
        debit: transaction.debit ? `€${transaction.debit}` : "€0.0",
        details: transaction.details || "",
      });
    }
  }, [transaction]);

  // Keep form customerName in sync with provided prop (non-editable in CustomerDetails)
  useEffect(() => {
    if (customerName) {
      setFormData((prev) => ({ ...prev, customerName }));
    }
  }, [customerName]);

  // Keep select synced with provided customerId
  useEffect(() => {
    if (customerId) {
      setSelectedCustomerId(customerId);
    }
  }, [customerId]);

  // Load customers for dropdown
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const result = await customersAPI.getCustomers("", 1, 50);
        const raw = result?.response ?? result;
        const list = Array.isArray(raw) ? raw : (raw?.results ?? raw?.data ?? []);
        const options = (list || [])
          .map((c) => ({
            id: c.id ?? c.customer_id ?? c.pk,
            name: c.name ?? c.customer_name ?? c.user_name ?? "Unknown",
          }))
          .filter((o) => o.id);
        setCustomerOptions(options);
      } catch (e) {
        console.error("Failed to load customers for dropdown", e);
      }
    };
    loadCustomers();
  }, []);

  // Date picker functions
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return date > today;
  };

  const isDateSelected = (date) => {
    if (!selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;
    setSelectedDate(date);
    setFormData((prev) => ({
      ...prev,
      date: date.toLocaleDateString("en-GB"),
    }));
    setShowDatePicker(false);
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };

      // Implement mutual exclusivity: if credit has value, clear debit and vice versa
      if (name === 'credit' && value.trim() && value !== "€0.0" && value !== "€0") {
        newData.debit = "";
      } else if (name === 'debit' && value.trim() && value !== "€0.0" && value !== "€0") {
        newData.credit = "";
      }

      return newData;
    });

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!customerId && !selectedCustomerId) {
      newErrors.customerName = "Customer is required";
    }

    // Check if at least one of credit or debit has a value
    const hasCredit = formData.credit.trim() && formData.credit !== "€0.0" && formData.credit !== "€0";
    const hasDebit = formData.debit.trim() && formData.debit !== "€0.0" && formData.debit !== "€0";

    if (!hasCredit && !hasDebit) {
      newErrors.credit = "Either Credit or Debit amount is required";
      newErrors.debit = "Either Credit or Debit amount is required";
    } else {
      // Validate credit if it has a value
      if (hasCredit) {
        const creditAmount = parseFloat(formData.credit.replace(/[€,]/g, ""));
        if (isNaN(creditAmount) || creditAmount <= 0) {
          newErrors.credit = "Please enter a valid positive credit amount";
        }
      }

      // Validate debit if it has a value
      if (hasDebit) {
        const debitAmount = parseFloat(formData.debit.replace(/[€,]/g, ""));
        if (isNaN(debitAmount) || debitAmount <= 0) {
          newErrors.debit = "Please enter a valid positive debit amount";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Parse date from DD/MM/YYYY to YYYY-MM-DD
      const [day, month, year] = formData.date.split('/');
      const formattedDate = `${year}-${month}-${day}`;

      const cleanedData = {
        selectedCustomerId: selectedCustomerId || customerId || null,
        credit: parseFloat(formData.credit.replace(/[€,]/g, "")),
        debit: parseFloat(formData.debit.replace(/[€,]/g, "")),
        details: formData.details.trim(),
        date: formattedDate,
      };

      await onSave(cleanedData);
    } catch (error) {
      console.error("Error saving finance transaction:", error);
      alert(
        "An error occurred while saving the transaction. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="finance-modal-overlay">
      <div className="finance-modal no-scrollbar">
        <div className="finance-modal-header">
          <div className="finance-modal-header-content">
            <h3 className="finance-modal-title">
              {isEditing ? "Edit Finance History" : "Add New Transaction"}
            </h3>
            <p className="finance-modal-subtitle">
              {isEditing
                ? "Edit and update finance history details below"
                : "Fill in the transaction details below to add new transaction"}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="finance-modal-form">
          <div className="finance-modal-body">
            <div className="finance-form-group">
              <label htmlFor="date" className="finance-form-label">
                Date *
              </label>
              <div className="finance-input-container" style={{ position: 'relative' }} ref={datePickerRef}>
                <Calendar 
                  size={16} 
                  className="finance-input-icon" 
                  style={{ cursor: isEditing ? 'not-allowed' : 'pointer', zIndex: 1 }}
                  onClick={() => !isEditing && setShowDatePicker(!showDatePicker)}
                />
                <input
                  type="text"
                  id="date"
                  name="date"
                  value={formData.date}
                  className="finance-input"
                  readOnly
                  disabled={isEditing}
                  onClick={() => !isEditing && setShowDatePicker(!showDatePicker)}
                  style={{ cursor: isEditing ? 'not-allowed' : 'pointer' }}
                />
                
                {showDatePicker && !isEditing && (
                  <div className="customer-detail-date-picker" style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 1000 }}>
                    <div className="date-picker-header">
                      <button
                        type="button"
                        className="date-picker-nav-btn"
                        onClick={() => navigateMonth(-1)}
                      >
                        ‹
                      </button>
                      <h3 className="date-picker-month-year">
                        {currentMonth.toLocaleDateString("en-US", {
                          month: "long",
                          year: "numeric",
                        })}
                      </h3>
                      <button
                        type="button"
                        className="date-picker-nav-btn"
                        onClick={() => navigateMonth(1)}
                      >
                        ›
                      </button>
                    </div>

                    <div className="date-picker-weekdays">
                      <div className="weekday">Mo</div>
                      <div className="weekday">Tu</div>
                      <div className="weekday">We</div>
                      <div className="weekday">Th</div>
                      <div className="weekday">Fr</div>
                      <div className="weekday">Sa</div>
                      <div className="weekday">Su</div>
                    </div>

                    <div className="date-picker-days">
                      {getDaysInMonth(currentMonth).map((day, index) => (
                        <div
                          key={index}
                          className={`date-picker-day ${
                            day ? "" : "empty"
                          } ${
                            day && isDateDisabled(day) ? "disabled" : ""
                          } ${
                            day && isDateSelected(day) ? "selected" : ""
                          }`}
                          onClick={() => day && handleDateClick(day)}
                        >
                          {day ? day.getDate() : ""}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="finance-form-group">
              <label htmlFor="customerSelect" className="finance-form-label">
                Customer *
              </label>
              <select
                id="customerSelect"
                name="customerSelect"
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                className={`finance-input ${
                  errors.customerName ? "finance-input-error" : ""
                }`}
                disabled={!!customerId}
                required={!customerId}
              >
                <option value="">Select customer</option>
                {customerOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
              {errors.customerName && (
                <div className="finance-error-message">{errors.customerName}</div>
              )}
            </div>

            <div className="finance-form-group">
              <label htmlFor="credit" className="finance-form-label">
                Credit *
              </label>
              <input
                type="text"
                id="credit"
                name="credit"
                value={formData.credit}
                onChange={handleInputChange}
                className={`finance-input ${
                  errors.credit ? "finance-input-error" : ""
                }`}
                placeholder="€0.0"
                disabled={formData.debit.trim() && formData.debit !== "€0.0" && formData.debit !== "€0"}
                required
              />
              {errors.credit && (
                <div className="finance-error-message">{errors.credit}</div>
              )}
            </div>

            <div className="finance-form-group">
              <label htmlFor="debit" className="finance-form-label">
                Debit *
              </label>
              <input
                type="text"
                id="debit"
                name="debit"
                value={formData.debit}
                onChange={handleInputChange}
                className={`finance-input ${
                  errors.debit ? "finance-input-error" : ""
                }`}
                placeholder="€0.0"
                disabled={formData.credit.trim() && formData.credit !== "€0.0" && formData.credit !== "€0"}
                required
              />
              {errors.debit && (
                <div className="finance-error-message">{errors.debit}</div>
              )}
            </div>

            <div className="finance-form-group">
              <label htmlFor="details" className="finance-form-label">
                Details
              </label>
              <textarea
                id="details"
                name="details"
                value={formData.details}
                onChange={handleInputChange}
                className="finance-textarea"
                placeholder="Enter details"
                rows={3}
              />
            </div>
          </div>

          <div className="finance-modal-footer">
            <button
              type="button"
              className="finance-btn finance-btn-cancel"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="finance-btn finance-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="finance-spinner"></span>
                  Saving...
                </>
              ) : isEditing ? (
                "Update Finance"
              ) : (
                "Add Transaction"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinanceTransactionForm;

